import { useUser } from '@utmb/contexts/AuthContext';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { LoginModal } from '@utmb/features/login-modal/LoginModal';

interface WorldSeriesRaceFollowProps {
    className?: string;
    raceId: number;
    onAfterUpdate?: () => void;
}

export const WorldSeriesRaceFollow = ({ raceId, className, onAfterUpdate }: WorldSeriesRaceFollowProps): ReactElement => {
    const { me, getToken, refreshMe } = useUser();
    const [optimisticFollowing, setOptimisticFollowing] = useState<boolean | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const isFollowing = useMemo(() => {
        return optimisticFollowing !== null ? optimisticFollowing : me && me.followed?.races?.includes(raceId);
    }, [me, me?.followed?.races, raceId, optimisticFollowing]);

    const handleClick = useCallback(async () => {
        if (!me) {
            setIsLoginModalOpen(true);
            return;
        }
        if (isUpdating) return;
        setIsUpdating(true);

        const currentFollowing = me && me.followed?.races?.includes(raceId);
        const newFollowing = !currentFollowing;

        setOptimisticFollowing(newFollowing);

        try {
            const token = await getToken();

            await fetchTheAPI({
                isClient: true,
                path: `/users/follow-race/${raceId}`,
                method: currentFollowing ? FetchMethodsEnum.DELETE : FetchMethodsEnum.POST,
                bearer: token,
            });

            await refreshMe();
            setOptimisticFollowing(null);
            if (onAfterUpdate) onAfterUpdate();
        } catch (error) {
            setOptimisticFollowing(null);
            console.error('Error on following/unfollowing', error);
        } finally {
            setIsUpdating(false);
        }
    }, [isUpdating, me, raceId, getToken, refreshMe, onAfterUpdate]);

    return (
        <>
            <div className={className} onClick={handleClick} onKeyDown={handleClick} role="button" style={{ cursor: 'pointer' }} tabIndex={0}>
                {isFollowing ? <Icon variant={IconsEnum.STAR} height={24} width={24} /> : <Icon variant={IconsEnum.STAR_BORDER} height={24} width={24} />}
            </div>
            <LoginModal isOpen={isLoginModalOpen} close={() => setIsLoginModalOpen(false)} />
        </>
    );
};
